import { useMutation, useQueryClient } from 'react-query';
import { enviarOktonBank } from '../oktonbank';
import {
  BANCO_BRADESCO,
  BANCO_SANTANDER,
  BANCO_SICOOB,
  BancoExtratoApi,
} from './useContasBancarias';

export interface NovoLancamentoExtrato {
  conta: string;
  data: string;
  // HH:mm, opcional; vazia grava 00:00.
  hora?: string;
  descricao: string;
  valor: string;
  // Lançamento manual não tem FITID; a referência é opcional.
  referencia?: string;
}

export interface ResultadoImportacao {
  inseridos: number;
  ignorados: number;
  descartados: number;
  // Dos que já existiam, quantos estavam sem hora e ganharam a que veio.
  horaspreenchidas?: number;
}

export interface ResultadoConsultaExtratoApi extends ResultadoImportacao {
  total: number;
}

export interface ConsultaExtratoApi {
  conta: string;
  ano: number;
  mes: number;
  diaInicial?: number;
  diaFinal?: number;
}

function invalidarExtrato(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries('extrato-bancario');
  queryClient.invalidateQueries('dashboard');
}

export function useCreateLancamentoExtrato() {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: NovoLancamentoExtrato) =>
      enviarOktonBank('OBExtrato', 'cadastrar', { ...payload }),
    { onSuccess: () => invalidarExtrato(queryClient) },
  );
}

export function useDeleteLancamentoExtrato() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => enviarOktonBank('OBExtrato', 'excluir', { extrato: id }),
    { onSuccess: () => invalidarExtrato(queryClient) },
  );
}

// O OFX é lido no navegador; aqui vai só o lote já parseado, como JSON num
// campo do formulário.
export function useImportarOfx() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ conta, lancamentos }: { conta: string; lancamentos: unknown[] }) =>
      enviarOktonBank<ResultadoImportacao>('OBExtrato', 'importar', {
        conta,
        lancamentos: JSON.stringify(lancamentos),
      }),
    { onSuccess: () => invalidarExtrato(queryClient) },
  );
}

const ACAO_EXTRATO_API: Record<BancoExtratoApi, string> = {
  [BANCO_SICOOB]: 'consultarSicoob',
  [BANCO_BRADESCO]: 'consultarBradesco',
  [BANCO_SANTANDER]: 'consultarSantander',
};

// O backend busca o extrato direto na API do banco (Sicoob, Bradesco ou
// Santander) e já grava: nada é parseado no navegador, diferente da
// importação de OFX.
export function useConsultarExtratoApi(banco: BancoExtratoApi) {
  const queryClient = useQueryClient();
  const action = ACAO_EXTRATO_API[banco];

  return useMutation(
    (payload: ConsultaExtratoApi) =>
      enviarOktonBank<ResultadoConsultaExtratoApi>('OBExtrato', action, {
        ...payload,
        diaInicial: payload.diaInicial ?? '',
        diaFinal: payload.diaFinal ?? '',
      }),
    { onSuccess: () => invalidarExtrato(queryClient) },
  );
}
